import { sendFBInitiateCheckout, sendFBPurchase, sendCompleteRegistration } from './api';
import { rinoLogEvent } from './rinoAnalytics';
import { currencyCode } from '../utils/currencySign';
import { isSandboxEnvironment } from '../utils/environment';

function generateEventId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'ev-' + Date.now() + '-' + Math.random().toString(36).slice(2);
}

function currentEventSourceUrl() {
    try {
        return typeof window !== 'undefined' ? window.location.href : null;
    } catch (e) {
        return null;
    }
}

const AMPLITUDE_API_KEY = "3d5f3f0b291a77a6af86c91549a90826";

let amplitudeReady = null;

function waitForAmplitudeScript(timeoutMs = 5000) {
    if (window.amplitude) return Promise.resolve(true);

    return new Promise((resolve) => {
        const start = Date.now();
        const interval = setInterval(() => {
            if (window.amplitude) {
                clearInterval(interval);
                resolve(true);
            } else if (Date.now() - start > timeoutMs) {
                clearInterval(interval);
                console.warn('Amplitude script failed to load in time');
                resolve(false);
            }
        }, 100);
    });
}

// Amplitude is initialized lazily, right before the single event we report.
function ensureAmplitude() {
    if (amplitudeReady) return amplitudeReady;

    amplitudeReady = (async () => {
        if (!(await waitForAmplitudeScript())) return false;
        try {
            await window.amplitude.init(AMPLITUDE_API_KEY, {
                fetchRemoteConfig: false,
                autocapture: false,
            }).promise;
            console.log('Amplitude initialized successfully');
            return true;
        } catch (e) {
            console.warn('Amplitude initialization failed', e);
            return false;
        }
    })();

    return amplitudeReady;
}

function fbq(...args) {
    if (isSandboxEnvironment()) return;
    if (typeof window.fbq === 'function') {
        window.fbq(...args);
    }
}

// purchase_completed_success is the only event reported to Amplitude.
async function amplitudeTrackPurchaseCompleted(props) {
    if (isSandboxEnvironment()) return;
    if (!(await ensureAmplitude())) return;
    window.amplitude.track('purchase_completed_success', props);
    window.amplitude.flush();
    console.log('📊 Amplitude: purchase_completed_success', props);
}

function dataLayerPush(item) {
    if (isSandboxEnvironment()) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(item);
    console.log(`📊 DataLayer: ${item.event}`, item);
}

// Server-side rino logging, suppressed entirely in sandbox.
function rinoTrack(event, props) {
    if (isSandboxEnvironment()) return;
    rinoLogEvent(event, props);
}

export function trackPageView() {
    fbq('track', 'PageView');
}

export function trackAddToCart() {
    fbq('track', 'AddToCart');
}

export function trackCompleteRegistration(email, fbc) {
    const eventId = generateEventId();
    const eventSourceUrl = currentEventSourceUrl();
    fbq('track', 'CompleteRegistration', {}, { eventID: eventId });
    if (!isSandboxEnvironment()) {
        sendCompleteRegistration(email, fbc, eventId, eventSourceUrl);
    }
}

export function trackScreenPresented({ screenCid, onboardingId }) {
    const props = {
        screen_cid: screenCid,
        funnelID: onboardingId,
        engine: 'rino',
    };
    rinoTrack('screen_presented', props);
    dataLayerPush({ event: 'screen_presented', ...props });
}

export function trackScreenCompleted({ screenCid, onboardingId, reply }) {
    const props = {
        screen_cid: screenCid,
        funnelID: onboardingId,
        engine: 'rino',
        ...(reply ? { reply } : {}),
    };
    rinoTrack('screen_completed', props);
    dataLayerPush({ event: 'screen_completed', ...props });
}

export function trackResetOnFirstScreen({ screenCid, onboardingId }) {
    const props = {
        screen_cid: screenCid,
        funnelID: onboardingId,
        engine: 'rino',
    };
    rinoTrack('onb_resetOnFirstScreen', props);
    dataLayerPush({ event: 'onb_resetOnFirstScreen', ...props });
}

export function trackInitiateCheckout(email) {
    const eventId = generateEventId();
    const eventSourceUrl = currentEventSourceUrl();
    fbq('track', 'InitiateCheckout', {}, { eventID: eventId });
    if (!isSandboxEnvironment()) {
        sendFBInitiateCheckout(email, eventId, eventSourceUrl);
    }
}

export function trackPurchaseStarted({ type, onboardingId, priceID, discountID }) {
    const dlItem = {
        event: 'purchase_started',
        type,
        funnelID: onboardingId,
        priceID,
        discountID,
    };
    dataLayerPush(dlItem);
    rinoTrack('purchase_started', {
        engine: 'rino',
        type,
        funnelID: onboardingId,
        priceID,
        discountID,
    });
}

export function trackPurchaseCompleted({ onboardingId, priceID, discountID, price, email }) {
    const value = price / 100;
    const eventId = generateEventId();
    const eventSourceUrl = currentEventSourceUrl();
    const currency = currencyCode()

    fbq('track', 'Purchase', { currency: currency, value: value }, { eventID: eventId });
    if (!isSandboxEnvironment()) {
        sendFBPurchase(email, value, eventId, eventSourceUrl);
    }

    dataLayerPush({
        event: 'purchase_completed_success',
        engine: 'rino',
        funnelID: onboardingId,
        priceID,
        discountID,
        value: value,
        currency: currency,
        email,
    });

    const ampProps = {
        engine: 'rino',
        funnelID: onboardingId,
        priceID,
        discountID,
    };
    amplitudeTrackPurchaseCompleted(ampProps);
    rinoTrack('purchase_completed_success', ampProps);
}

export function trackPurchaseFailed({ onboardingId, paymentProvider, data }) {
    dataLayerPush({
        event: 'purchase_failed',
        funnelID: onboardingId,
        paymentProvider: paymentProvider || 'stripe',
        data,
    });
    rinoTrack('purchase_failed', {
        engine: 'rino',
        funnelID: onboardingId,
        paymentProvider: paymentProvider || 'stripe',
        data,
    });
}

export function trackPdfPurchaseFailed({ onboardingId, email, paymentProvider, data }) {
    dataLayerPush({
        event: 'pdf_purchase_failed',
        funnelID: onboardingId,
        email,
        paymentProvider: paymentProvider || 'stripe',
        data,
    });
    rinoTrack('pdf_purchase_failed', {
        engine: 'rino',
        funnelID: onboardingId,
        email,
        paymentProvider: paymentProvider || 'stripe',
        data,
    });
}
